// @ts-nocheck
import { interactivityRegistration } from "./Charta";
import { DomQuery, HtmlClassSwitch, WithClass } from "./Dom";
import { application } from "./WebApp.Instance";
import { PageMode } from "./WebApp.Page";
import { ToastMessage } from "./WebApp.ToastBox";
import { Form } from "./WebSite.Form";
import { ControlMode } from "./WebSite.Page.Component";

export class ProblemTarget {
  constructor() {
    this.problems = new Map();
  }

  clearProblems(field) {
    this.problems.set(field, new Array());
  }

  addProblem(field, problem) {
    this.problems.get(field).push(problem);
  }

  isValid() {
    return this.someValid() && Array.from(this.problems.values()).every(problems => problems.length === 0);
  }

  someValid() {
    return Array.from(this.problems.entries()).some(
      ([field, problems]) => {
        const value = field.getValue();
        return (!field.optional || (value !== null && value !== "")) && problems.length === 0;
      }
    )
  }
}

export class WizardFormSection {
  constructor(wizard, selector, section) {
    this.wizard = wizard;
    this.selector = selector;
    this.section = section;

    const formSection = new DomQuery(this.section).getDescendant(WithClass("FormSection")).component;

    if (formSection !== null)
      this.fields = formSection.childComponents;
    else
      this.fields = new Array();

    this.inProgress = new HtmlClassSwitch(this.selector, "InProgress");
    this.completed = new HtmlClassSwitch(this.selector, "Completed");

    this.attachHandlers();
    this.validate(false);
  }

  attachHandlers() {
    for (const field of this.fields)
      field.addEventListener("change", (event) => this.validate(true));
  }

  validate(recalculate) {
    if (this.wizard.mode === ControlMode.edit) {
      const problems = new ProblemTarget();

      for (const field of this.fields)
        field.validate(problems);

      this.completed.setStatus(problems.isValid());
      this.inProgress.setStatus(!this.completed.getStatus() && problems.someValid());

      if (recalculate)
        this.wizard.recalculate();
    }
  }

  isInProgress() {
    return this.fields.some(element => element.inputOk);
  }

  isCompleted() {
    return this.fields.every(element => element.inputOk);
  }

  load(value) {
    for (const property in value) {
      const field = this.fields.find(element => element.name === property);

      if (field && field.mode === ControlMode.edit)
        field.load(value[property]);
    }
  }

  save() {
    const result = {};

    for (const field of this.fields)
      if (field.mode === ControlMode.edit)
        result[field.name] = field.save();

    return result;
  }

  select() {
    this.selector.classList.add("Active");
    this.section.classList.add("Active");

    this.validate(false);
  }

  deselect() {
    this.selector.classList.remove("Active");
    this.section.classList.remove("Active");

    this.validate(false);
  }

  get name() {
    return this.section.dataset.Name;
  }
}

export class WizardForm extends Form {
  constructor(element) {
    super(element);

    this.sections = new Array();
    this.pristine = null;
    this.modified = new HtmlClassSwitch(this.element, "Modified");
    this.saving = new HtmlClassSwitch(this.element, "Saving");
  }

  bind() {
    this.initializeSections();
    this.initializeToolbar();

    this.sections[0].select();
    this.active = this.sections[0];
  }

  load(value) {
    for (const property in value) {
      const section = this.sections.find(element => element.name === property);

      if (section)
        section.load(value[property]);
    }

    this.pristine = this.save();
  }

  save() {
    const result = {};

    for (const section of this.sections)
      result[section.name] = section.save();

    return result;
  }

  async recalculate() {
    if (this.mode === ControlMode.edit && this.pristine !== null) {
      const pristine = JSON.stringify(this.pristine);
      const current = JSON.stringify(this.save());

      if (pristine !== current) {
        if (application.pageHandler.mode === PageMode.Offline)
          await this.persist();
        else
          this.modified.setStatus(pristine !== current);
      }
    }
  }

  async persist() {
    this.saving.setStatus(true);

    try {
      await application.pageHandler.persistOfflinePage();

      this.pristine = this.save();
      this.modified.setStatus(false);
    } finally {
      setTimeout(
        () => {
          this.saving.setStatus(false);
        },
        250
      );
    }
  }

  async submit() {
    if (application.pageHandler.mode === PageMode.Online)
      this.submitAsynchronous();
    else {
      await this.persist();
      application.toastBox.addMessage(new ToastMessage(this.labels.SuccessfulSubmission, "Success"));
    }
  }

  initializeSections() {
    const selectorsElement = new DomQuery(this.element).getChild(WithClass("Sections"));
    const selectorElements = new DomQuery(selectorsElement).getChildren(WithClass("Section"));

    for (let index = 0; index < selectorElements.length; index++) {
      const selectorElement = selectorElements[index];

      const section = new WizardFormSection(this, selectorElement, new DomQuery(this.element).getChild((element) => { return selectorElement.dataset.Name === element.dataset.Name }));
      section.selector.addEventListener(
        "click",
        (event) => {
          this.select(section);
        }
      );

      this.sections.push(section);
    }
  }

  initializeToolbar() {
    this.toolbar = document.createElement("div");
    this.toolbar.classList.add("Toolbar");

    this.previousButton = this.addButton("Previous", (event) => this.previous());

    if (this.mode === ControlMode.edit)
      this.submitButton = this.addButton("Submit", (event) => this.submit());

    this.nextButton = this.addButton("Next", (event) => this.next());

    this.element.appendChild(this.toolbar);
  }

  next() {
    const index = this.sections.indexOf(this.active);

    if (index < this.sections.length - 1)
      this.select(this.sections[index + 1]);
  }

  previous() {
    const index = this.sections.indexOf(this.active);

    if (index > 0)
      this.select(this.sections[index - 1]);
  }

  addButton(className, action) {
    const button = document.createElement("button");
    button.classList.add(className);
    button.type = "button";

    if (action)
      button.addEventListener("click", action);

    this.toolbar.appendChild(button);
    return button;
  }

  select(target) {
    this.active.validate(false);

    this.sections.forEach(
      (section) => {
        if (section !== target)
          section.deselect();
      }
    );

    target.select();

    this.active = target;
    this.active.section.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });

    this.previousButton.disabled = this.active === this.sections[0];
    this.nextButton.disabled = this.active === this.sections[this.sections.length - 1];
  }

  get name() {
    return this.element.dataset.Name;
  }
}

const formEntry = interactivityRegistration.entries.find(entry => entry._class === "Form");

if (formEntry) {
  const originalConstructor = formEntry._constructor;
  formEntry._constructor = (element) => element.classList.contains("Wizard") ? new WizardForm(element) : originalConstructor(element);
}
